/**
 * MMOLootDrop - Display class for combat loot drops on the hex map.
 * Shows a glowing wreckage icon that players can click to claim.
 */

import Phaser from 'phaser';
import { axialToPixel, type HexCoord } from '../../shared/HexUtils';

export interface LootDropData {
  id: string;
  position: HexCoord;
  sourceUnitType: string;
  hasComponent: boolean;
  hasMinerals: boolean;
  hasGold: boolean;
  topDealerId: string | null;
  exclusiveUntil: string;
  expiresAt: string;
}

const SHIP_ICON_MAP: Record<string, string> = {
  scout: 'ship-scout',
  fighter: 'ship-fighter',
  destroyer: 'ship-destroyer',
  bomber: 'ship-bomber',
  cruiser: 'ship-cruiser',
  carrier: 'ship-carrier',
  battleship: 'ship-battleship',
  dreadnought: 'ship-dreadnought',
  titan: 'ship-titan',
  space_tug: 'ship-space-tug',
};

export class MMOLootDrop extends Phaser.GameObjects.Container {
  public id: string;
  public hexCoord: HexCoord;
  public sourceUnitType: string;
  public hasComponent: boolean;
  public hasMinerals: boolean;
  public hasGold: boolean;
  public topDealerId: string | null;
  public exclusiveUntil: string;
  public expiresAt: string;
  public entityType = 'loot_drop';

  private glowGraphics: Phaser.GameObjects.Graphics;
  private entityGraphics: Phaser.GameObjects.Graphics;
  private selectionIndicator: Phaser.GameObjects.Graphics;
  private nameLabel: Phaser.GameObjects.Text;
  private glowTween: Phaser.Tweens.Tween | null = null;
  private lootSprite: Phaser.GameObjects.Image | null = null;

  constructor(scene: Phaser.Scene, data: LootDropData) {
    const pixel = axialToPixel(data.position.q, data.position.r);
    super(scene, pixel.x, pixel.y);

    this.id = data.id;
    this.hexCoord = data.position;
    this.sourceUnitType = data.sourceUnitType || 'unknown';
    this.hasComponent = data.hasComponent;
    this.hasMinerals = data.hasMinerals;
    this.hasGold = data.hasGold;
    this.topDealerId = data.topDealerId;
    this.exclusiveUntil = data.exclusiveUntil;
    this.expiresAt = data.expiresAt;

    // Selection ring
    this.selectionIndicator = this.scene.add.graphics();
    this.selectionIndicator.lineStyle(2, 0xffaa00, 0.8);
    this.selectionIndicator.strokeCircle(0, 0, 18);
    this.selectionIndicator.setVisible(false);
    this.add(this.selectionIndicator);

    // Pulsing glow behind icon
    this.glowGraphics = this.scene.add.graphics();
    this.add(this.glowGraphics);

    // Main visual
    this.entityGraphics = this.scene.add.graphics();
    this.add(this.entityGraphics);

    // Label
    const labelParts: string[] = [];
    if (data.hasComponent) labelParts.push('COMP');
    if (data.hasMinerals) labelParts.push('ORE');
    if (data.hasGold) labelParts.push('TOKENS');
    const labelText = labelParts.length > 0 ? labelParts.join('+') : 'LOOT';

    this.nameLabel = this.scene.add.text(0, 20, labelText, {
      fontSize: '7px',
      color: '#ffcc44',
      fontFamily: "'Share Tech Mono', monospace",
    });
    this.nameLabel.setOrigin(0.5);
    this.nameLabel.setVisible(false);
    this.add(this.nameLabel);

    this.drawVisuals();
    this.setDepth(4); // Between wrecks(3) and planets(5)

    // Expand hit-zone to 48px minimum on touch devices (iOS/Android guidelines)
    const _isCoarseLoot = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(pointer: coarse)').matches;
    const _lootHit = _isCoarseLoot ? 48 : 40;
    this.setSize(_lootHit, _lootHit);
    this.setInteractive(
      new Phaser.Geom.Rectangle(-_lootHit / 2, -_lootHit / 2, _lootHit, _lootHit),
      Phaser.Geom.Rectangle.Contains,
    );

    // NOTE: No sprite-level 'pointerdown' handler — see MMOUnit.ts for rationale.
    // Selection is handled in MMOGameScene.onPointerUp via getEntityAtWorldPoint().

    // Pulsing glow animation
    this.glowTween = this.scene.tweens.add({
      targets: this.glowGraphics,
      alpha: { from: 0.25, to: 0.7 },
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  public updateFromServer(data: LootDropData): void {
    if (!this.scene || !this.scene.sys) return;

    this.hasComponent = data.hasComponent;
    this.hasMinerals = data.hasMinerals;
    this.hasGold = data.hasGold;
    this.topDealerId = data.topDealerId;
    this.exclusiveUntil = data.exclusiveUntil;
    this.expiresAt = data.expiresAt;

    const newPos = data.position;
    if (newPos.q !== this.hexCoord.q || newPos.r !== this.hexCoord.r) {
      this.hexCoord = newPos;
      const pixel = axialToPixel(newPos.q, newPos.r);
      this.setPosition(pixel.x, pixel.y);
    }
  }

  private drawVisuals(): void {
    // Amber/gold glow for loot
    this.glowGraphics.clear();
    this.glowGraphics.fillStyle(0xffaa00, 0.12);
    this.glowGraphics.fillCircle(0, 0, 16);
    this.glowGraphics.fillStyle(0xffaa00, 0.06);
    this.glowGraphics.fillCircle(0, 0, 22);

    // Try ship sprite (tinted for wreckage look)
    const unitType = this.sourceUnitType.toLowerCase().replace(/ /g, '_');
    const spriteKey = SHIP_ICON_MAP[unitType];
    if (spriteKey && this.scene.textures.exists(spriteKey)) {
      this.entityGraphics.clear();
      this.entityGraphics.setVisible(false);

      if (!this.lootSprite) {
        this.lootSprite = this.scene.add.image(0, 0, spriteKey);
        this.lootSprite.setDisplaySize(22, 22);
        this.lootSprite.setTint(0xffaa44);
        this.lootSprite.setAlpha(0.8);
        this.add(this.lootSprite);
        this.moveTo(this.lootSprite, 2);
      }
    } else {
      this.drawFallbackIcon();
    }
  }

  private drawFallbackIcon(): void {
    this.entityGraphics.clear();
    this.entityGraphics.setVisible(true);

    // Draw a loot crate icon (square with X)
    this.entityGraphics.fillStyle(0xffaa00, 0.7);
    this.entityGraphics.fillRect(-8, -8, 16, 16);
    this.entityGraphics.lineStyle(1.5, 0xffcc44, 1);
    this.entityGraphics.strokeRect(-8, -8, 16, 16);

    // X mark
    this.entityGraphics.lineStyle(1, 0x442200, 0.6);
    this.entityGraphics.lineBetween(-5, -5, 5, 5);
    this.entityGraphics.lineBetween(-5, 5, 5, -5);
  }

  public setSelected(selected: boolean): void {
    this.selectionIndicator.setVisible(selected);
  }

  public setVisibility(fullyVisible: boolean, explored: boolean): void {
    if (!explored) {
      this.setAlpha(0);
    } else if (!fullyVisible) {
      this.setAlpha(0.3);
    } else {
      this.setAlpha(1);
    }
  }

  public destroy(fromScene?: boolean): void {
    if (this.glowTween) {
      this.glowTween.destroy();
      this.glowTween = null;
    }
    if (this.lootSprite) {
      this.lootSprite.destroy();
      this.lootSprite = null;
    }
    super.destroy(fromScene);
  }
}
